/**
 * GAUCHO-I18N-1 — Gaucho copy, labels & UX polish (EN).
 *
 * Pure translations. No logic. Every user-facing label is:
 *  - evidence-gated
 *  - review-oriented, not execution-oriented
 *  - safety-aware (do-not-conclude clauses only in negative context)
 *  - consistent with the resource-efficiency positioning
 */

export const gauchoEn = {
  gauchoDailyBrief: {
    status: {
      blocked: "Blocked",
      attention_needed: "Attention needed",
      insufficient_evidence: "Insufficient evidence",
      normal: "Normal",
    },
    headline: {
      blocked:
        "Today's brief: irrigation or critical decisions blocked pending evidence.",
      attention_needed: "Today's brief: attention needed on this parcel.",
      insufficient_evidence:
        "Today's brief: insufficient evidence to prioritize actions.",
      normal: "Today's brief: no critical risks detected.",
    },
    summary: {
      blocked:
        "One or more agronomic decisions are blocked because required evidence is missing or contradictory. Review the blocked items, upload the missing evidence and validate in field before acting. Nothing is executed automatically.",
      attention_needed:
        "There are active risks or open priorities that require human review today. Confirm the observations, validate in field and update the field notebook. Nothing is executed automatically.",
      insufficient_evidence:
        "There is not enough recent evidence on this parcel to build reliable priorities. Upload recent field observations, irrigation logs and soil/foliar evidence to improve the brief.",
      normal:
        "No critical risks or blocked decisions detected with current evidence. Continue routine field observation and documentation.",
    },
    risks: {
      fire: { summary: "Satellite hotspots near the parcel — review official emergency sources." },
      frost: { summary: "Low-temperature forecast — validate locally before any protection decision." },
      pest_disease: { summary: "Possible pest or disease pressure reported — field scouting required." },
      water_stress: { summary: "Water-stress signal detected — validate in field before any irrigation decision." },
      alerts: { summary: "Active parcel alerts pending human review." },
    },
    priorities: {
      generic: {
        title: "Priority item pending review",
        desc: "Requires human review. Not executed automatically.",
      },
    },
    tasks: {
      generic: {
        title: "Suggested task pending review",
        desc: "Suggested task. Requires human review. Not executed automatically.",
      },
    },
    blockedDecisions: {
      irrigation_need: { reason: "Irrigation need blocked — required evidence is missing or contradictory." },
      irrigation_amount_by_zone: { reason: "Zone irrigation amount blocked — FAO-56 evidence gates not satisfied." },
      irrigation_timing: { reason: "Irrigation timing blocked — forecast or field evidence missing." },
      scouting_priority_by_zone: { reason: "Scouting priority blocked — field validation required." },
      fertilization_recommendation_n: { reason: "Nitrogen recommendation blocked — soil/foliar or lifecycle evidence missing." },
      fertilization_recommendation_pk: { reason: "P & K recommendation blocked — soil analysis missing." },
      vra_export_readiness: { reason: "VRA export blocked — required evidence missing." },
      report_generation_requirement: { reason: "Report generation blocked — supporting evidence missing." },
      compliance_evidence_check: { reason: "Compliance evidence check blocked — records incomplete." },
      lifecycle_planning_questions: { reason: "Lifecycle planning blocked — producer confirmation missing." },
      frost_protection_prep: { reason: "Frost protection preparation blocked — local validation required." },
      drought_outlook: { reason: "Drought outlook blocked — forecast or field evidence missing." },
    },
  },

  gauchoPriorities: {
    fire: {
      review_official_sources: {
        title: "Review official fire sources",
        desc: "Satellite hotspots detected near the parcel. Cross-check with official emergency sources (112, Protección Civil, national fire agency) before any decision. Does not confirm active fire.",
      },
    },
    frost: {
      validate_local: {
        title: "Validate frost risk locally",
        desc: "Low-temperature forecast reported. Compare with your on-site sensor or nearest station before deciding on any protection measure.",
      },
    },
    pest: {
      scout_symptoms: {
        title: "Scout pest / disease symptoms",
        desc: "Field scouting is required to confirm any pest or disease signal. Does not confirm diagnosis and does not recommend any product, active ingredient or dose.",
      },
    },
    waterStress: {
      validate: {
        title: "Validate water-stress signal in field",
        desc: "Satellite / model signal suggests possible water stress. Validate in field and check the irrigation log before considering any irrigation decision.",
      },
    },
    alerts: {
      review_general: {
        title: "Review active parcel alerts",
        desc: "One or more alerts are active on this parcel. Open the Alerts Center, validate supporting evidence and decide next steps manually.",
      },
    },
    ndvi: {
      inspect_low_vigor: {
        title: "Inspect low-vigor NDVI zone",
        desc: "A low-vigor zone was detected. Field validation is required before inferring cause. NDVI alone does not justify irrigation, fertilization or crop-protection changes.",
      },
    },
    soilFoliar: {
      soil_lab_analysis: {
        title: "Upload recent soil analysis",
        desc: "A recent soil lab analysis is missing. Upload the latest report to improve documentation. Does not generate any product or dose recommendation.",
      },
      foliar_analysis: {
        title: "Upload recent foliar analysis",
        desc: "A recent foliar analysis is missing. Upload the latest report to improve documentation. Does not generate any product or dose recommendation.",
      },
    },
    irrigation: {
      irrigation_log: {
        title: "Confirm recent irrigation",
        desc: "Recent irrigation events are not documented for this parcel. Confirm what was applied (or that no irrigation was applied) in the field notebook. FAO-56 gates are not relaxed.",
      },
      no_irrigation_confirmation: {
        title: "Confirm no-irrigation status",
        desc: "Please confirm whether the parcel received no irrigation in the current window. This confirmation is required to keep the irrigation decision unblocked.",
      },
    },
    forecast: {
      review_7d: {
        title: "Review 7-day weather forecast",
        desc: "The 7-day forecast is missing or stale. Refresh the forecast before considering irrigation timing or amount. Forecast is not certainty.",
      },
    },
    lifecycle: {
      harvest_status: {
        title: "Confirm harvest status",
        desc: "Please confirm whether the primary crop has been harvested. Required for lifecycle-dependent decisions.",
      },
      next_crop: {
        title: "Declare next planned crop",
        desc: "Please indicate the next planned crop for this parcel. Required for rotation planning. No sowing or planting is executed automatically.",
      },
      post_harvest_objective: {
        title: "Declare post-harvest objective",
        desc: "Please declare your main post-harvest objective (nitrogen fixation, erosion control, soil structure, etc.). Required for rotation planning.",
      },
    },
    producer: {
      confirm_harvest_status: {
        title: "Producer confirmation: harvest status",
        desc: "Please answer the required question about harvest status.",
      },
      declare_next_crop: {
        title: "Producer confirmation: next planned crop",
        desc: "Please answer the required question about the next planned crop.",
      },
      declare_post_harvest_objective: {
        title: "Producer confirmation: post-harvest objective",
        desc: "Please answer the required question about the post-harvest objective.",
      },
      confirm_no_irrigation: {
        title: "Producer confirmation: no-irrigation",
        desc: "Please confirm whether the parcel received no irrigation in the current window.",
      },
      confirm_field_scouting_done: {
        title: "Producer confirmation: scouting done",
        desc: "Please confirm whether field scouting has been completed.",
      },
    },
    rotation: {
      document_inputs: {
        title: "Complete rotation planning inputs",
        desc: "Rotation planning inputs are incomplete. Provide the missing information (harvest, next crop, objective) to enable rotation guidance. Does not recommend any specific cover-crop species or mix.",
      },
    },
    docs: {
      document_observations: {
        title: "Document routine field observations",
        desc: "No urgent items today. Continue documenting routine field observations in the field notebook to maintain traceability.",
      },
    },
    blockedDecisions: {
      irrigation_need: {
        title: "Irrigation need blocked",
        desc: "Irrigation need cannot be quantified with current evidence. Review the missing evidence list and validate in field. No irrigation is executed automatically.",
      },
      irrigation_amount_by_zone: {
        title: "Irrigation amount blocked",
        desc: "Zone irrigation amount cannot be computed because FAO-56 gates are not satisfied. FAO-56 gates are never relaxed.",
      },
      irrigation_timing: {
        title: "Irrigation timing blocked",
        desc: "Irrigation timing cannot be recommended without a fresh 7-day forecast and applied-irrigation log.",
      },
      scouting_priority_by_zone: {
        title: "Scouting priority blocked",
        desc: "Scouting priority requires field validation before it can be finalized.",
      },
      fertilization_recommendation_n: {
        title: "Nitrogen recommendation blocked",
        desc: "Nitrogen recommendation is blocked pending soil/foliar analysis and lifecycle confirmation. Requires qualified agronomic review.",
      },
      fertilization_recommendation_pk: {
        title: "P & K recommendation blocked",
        desc: "P & K recommendation is blocked pending a recent soil lab analysis. Requires qualified agronomic review.",
      },
      vra_export_readiness: {
        title: "VRA export blocked",
        desc: "VRA export is blocked pending complete zone and evidence data.",
      },
      report_generation_requirement: {
        title: "Report generation blocked",
        desc: "Report generation is blocked because supporting evidence is incomplete.",
      },
      compliance_evidence_check: {
        title: "Compliance evidence check blocked",
        desc: "Compliance evidence check is blocked. Does not conclude compliance status.",
      },
      lifecycle_planning_questions: {
        title: "Lifecycle planning blocked",
        desc: "Lifecycle planning is blocked pending producer confirmation.",
      },
      frost_protection_prep: {
        title: "Frost protection preparation blocked",
        desc: "Frost protection preparation is blocked pending local validation. Does not activate machinery, heaters, wind machines or irrigation as frost protection.",
      },
      drought_outlook: {
        title: "Drought outlook blocked",
        desc: "Drought outlook is blocked pending fresh forecast and field evidence.",
      },
    },
  },

  gauchoTasks: {
    fire: {
      review_official_sources: {
        title: "Review official fire sources for this parcel",
        desc: "Cross-check satellite hotspots with official emergency sources. Does not confirm active fire. Does not replace 112 or Protección Civil.",
      },
    },
    frost: {
      validate_local: {
        title: "Validate frost forecast with local observation",
        desc: "Compare the frost forecast with your on-site sensor or nearest station. Forecast is not certainty.",
      },
    },
    pest: {
      scout_symptoms: {
        title: "Scout pest / disease symptoms in field",
        desc: "Walk the parcel and record symptoms in the field notebook. Does not confirm diagnosis. Does not recommend any product, active ingredient or dose.",
      },
    },
    ndvi: {
      validate_low_vigor: {
        title: "Validate low-vigor NDVI zone in field",
        desc: "Inspect the low-vigor zone in field. Do not infer cause from NDVI alone.",
      },
      inspect_low_vigor: {
        title: "Inspect low-vigor NDVI zone",
        desc: "Visit the low-vigor zone and document what you observe. Does not justify irrigation, fertilization or crop-protection changes on its own.",
      },
    },
    alerts: {
      review_general: {
        title: "Review active parcel alerts",
        desc: "Open the Alerts Center, review each alert and decide next steps manually.",
      },
    },
    soilFoliar: {
      upload_soil_lab_analysis: {
        title: "Upload recent soil lab analysis",
        desc: "Upload the latest soil analysis to keep documentation current. Does not recommend any fertilizer product or dose.",
      },
      upload_foliar_analysis: {
        title: "Upload recent foliar analysis",
        desc: "Upload the latest foliar analysis to keep documentation current. Does not recommend any fertilizer product or dose.",
      },
    },
    irrigation: {
      confirm_applied: {
        title: "Confirm recent applied irrigation",
        desc: "Record what was applied (or that no irrigation was applied) in the field notebook. FAO-56 gates are never relaxed.",
      },
    },
    forecast: {
      review_7d: {
        title: "Review 7-day weather forecast",
        desc: "Refresh the 7-day forecast before considering any irrigation-related decision.",
      },
    },
    lifecycle: {
      confirm_harvest_status: {
        title: "Confirm harvest status",
        desc: "Confirm whether the primary crop has been harvested.",
      },
      confirm_next_crop: {
        title: "Confirm next planned crop",
        desc: "Confirm the next planned crop for rotation planning.",
      },
      confirm_post_harvest_objective: {
        title: "Confirm post-harvest objective",
        desc: "Confirm the main post-harvest objective for this parcel.",
      },
    },
    producer: {
      confirm_harvest_status: {
        title: "Answer harvest-status question",
        desc: "Structured producer answer required.",
      },
      declare_next_crop: {
        title: "Answer next-crop question",
        desc: "Structured producer answer required.",
      },
      declare_post_harvest_objective: {
        title: "Answer post-harvest objective question",
        desc: "Structured producer answer required.",
      },
      confirm_no_irrigation: {
        title: "Answer no-irrigation question",
        desc: "Structured producer answer required.",
      },
      confirm_field_scouting_done: {
        title: "Answer scouting-status question",
        desc: "Structured producer answer required.",
      },
    },
    rotation: {
      document_inputs: {
        title: "Document rotation planning inputs",
        desc: "Complete the required inputs for rotation planning. Does not recommend any specific cover-crop species or mix.",
      },
    },
    docs: {
      document_observations: {
        title: "Document routine field observations",
        desc: "Log routine observations in the field notebook to maintain traceability.",
      },
    },
    reports: {
      review_compliance_evidence: {
        title: "Review compliance evidence",
        desc: "Open Reports Center and review the available compliance and audit evidence. Does not submit insurance, legal or PAC claims.",
      },
    },
    water: {
      validate_context: {
        title: "Validate water context in field",
        desc: "Cross-check satellite soil moisture with a field observation. Water context is informational — it never triggers irrigation execution.",
      },
    },
  },

  gauchoMemory: {
    confirmation: {
      generic: "Recent field confirmation recorded.",
      no_irrigation: "No-irrigation confirmation recorded.",
      irrigation_applied: "Irrigation-applied confirmation recorded.",
      harvest_status: "Harvest-status confirmation recorded.",
      next_crop: "Next-crop confirmation recorded.",
      post_harvest_objective: "Post-harvest objective confirmation recorded.",
      field_scouting: "Field-scouting confirmation recorded.",
      no_irrigation_confirmed: "No-irrigation confirmation recorded.",
      irrigation_uncertain: "Irrigation status uncertain — decision remains blocked.",
      lifecycle_uncertain: "Lifecycle status uncertain — decision remains blocked.",
      crop_active_confirmed: "Active crop confirmation recorded.",
      near_harvest_confirmed: "Near-harvest confirmation recorded.",
      harvested_confirmed: "Harvest confirmation recorded.",
      next_crop_declared: "Next-crop declaration recorded.",
      post_harvest_objective_declared: "Post-harvest objective declaration recorded.",
      field_validation_completed: "Field validation confirmation recorded.",
      scouting_completed: "Scouting completion recorded.",
    },
    fieldActivity: {
      generic: "Field activity recorded.",
      irrigation: "Irrigation activity recorded.",
      scouting: "Scouting activity recorded.",
      fertilization: "Fertilization activity recorded.",
      observation: "Field observation recorded.",
      soil_sampling: "Soil sampling recorded.",
      foliar_sampling: "Foliar sampling recorded.",
    },
    irrigation: {
      recorded: "Applied irrigation event recorded — historical context only.",
    },
    scouting: {
      generic: "Scouting record — historical context only.",
      pest: "Pest scouting record — historical context only. Does not confirm current pest.",
      disease: "Disease scouting record — historical context only. Does not confirm current disease.",
      weed: "Weed scouting record — historical context only.",
    },
    soil: {
      recorded: "Soil evidence recorded — historical context only. Does not imply any product or dose.",
    },
    foliar: {
      recorded: "Foliar evidence recorded — historical context only. Does not imply any product or dose.",
    },
    season: {
      planting: {
        generic: "Historical planting event recorded.",
      },
      harvest: {
        generic: "Historical harvest event recorded.",
      },
    },
    risk: {
      historicalFireAlert: "Historical fire alert — does not indicate a current emergency.",
      historicalFrostPrediction: "Historical frost prediction — does not confirm frost damage.",
      historicalAlert: {
        generic: "Historical alert — does not indicate a current condition.",
      },
      historicalNdviZonification: "Historical NDVI zonification — does not infer current cause.",
    },
    report: {
      generic: "Historical report record — does not conclude compliance, insurance or liability.",
    },
  },

  gauchoAutonomy: {
    sectionTitle: "Autonomy boundaries",
    sectionSubtitle:
      "Gaucho reasons, prioritizes and creates safe suggested tasks based on evidence. It does not execute critical agronomic, machinery, irrigation, chemical, emergency, legal, insurance or compliance actions.",
    activeRange:
      "Active autonomy: Levels L1–L3. L4 is available only for supervised preparation and human review, with no real-world execution. L5 remains disabled.",
    badge: {
      evidenceGated: "Evidence-gated",
      humanReview: "Requires human review",
      criticalBlocked: "Critical actions blocked",
    },
    module: {
      parcel_status: "Parcel status",
      fao56_irrigation: "FAO-56 irrigation evidence",
      applied_irrigation_log: "Applied irrigation log",
      ndvi_zones: "NDVI vigor zones",
      sar_contradiction: "SAR contradiction",
      fire_risk: "Fire risk",
      general_alerts: "General alerts",
      frost_risk: "Frost risk",
      pest_disease: "Pest & disease",
      soil_foliar: "Soil / foliar evidence",
      crop_lifecycle: "Crop lifecycle",
      crop_rotation: "Crop rotation",
      reports: "Reports & documentation",
      daily_brief: "Today's brief",
      memory: "Parcel memory",
      safe_tasks: "Suggested tasks",
      ranked_priorities: "Ranked priorities",
      vegetation_indices: "Vegetation indices",
      drought_water_stress: "Drought & water stress",
      phenology_intelligence: "Phenology intelligence",
      foliar_analysis: "Foliar analysis",
      soil_analysis: "Soil analysis",
    },
    blocked: {
      invalid_input: "Blocked: invalid input.",
      forbidden_capability: "Blocked: forbidden capability. Never allowed.",
      unknown_module: "Blocked: unknown module. Fail-closed.",
      module_blocked_capability: "Blocked: capability blocked by module policy.",
      capability_not_declared: "Blocked: capability not declared as allowed for this module.",
      level5_globally_disabled: "Blocked: Level 5 direct autonomous action is globally disabled.",
      level4_future_only: "Blocked: Level 4 human-approved workflow is future-only.",
      exceeds_current_level: "Blocked: exceeds the current active autonomy level.",
    },
    allowed: {
      within_module_policy: "Allowed within module policy. Requires human review.",
    },
  },
};

export default gauchoEn;